import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '@/lib/api'
import { formatRelative } from '@/lib/utils'

export function ApprovalQueue() {
  const qc = useQueryClient()

  const { data: approvals = [], isLoading } = useQuery({
    queryKey: ['approvals'],
    queryFn: api.approvals,
  })

  const pending = approvals.filter((a) => a.status === 'pending').slice(0, 4)

  const approveMut = useMutation({
    mutationFn: (id: string) => api.approve(id),
    onSuccess: (res, id) => {
      toast.success('Approved', {
        description: `WordPress webhook fired · delivery ${res.delivery_id.slice(0, 8)}`,
      })
      qc.invalidateQueries({ queryKey: ['approvals'] })
      qc.invalidateQueries({ queryKey: ['kpis'] })
      qc.invalidateQueries({ queryKey: ['articles'] })
      void id
    },
    onError: (err) => toast.error('Approval failed', { description: String(err) }),
  })

  return (
    <section className="border border-border rounded-md p-3 min-w-0">
      <div className="flex justify-between items-center mb-2.5">
        <h2 className="text-sm font-medium">Approval queue</h2>
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-status-approved-bg text-status-approved-fg">
          WP webhook ready
        </span>
      </div>

      {isLoading && <div className="text-xs text-ink-tertiary">Loading…</div>}

      <div className="flex flex-col gap-2">
        {pending.map((a) => (
          <article key={a.id} className="border border-border rounded-md px-2.5 py-2">
            <div className="text-sm font-medium leading-snug line-clamp-2">{a.article_title}</div>
            <div className="text-xs text-ink-secondary mt-0.5">
              {a.writer_name ?? a.agent_name} · {a.word_count.toLocaleString()} words · {formatRelative(a.created_at)}
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              <button
                onClick={() => approveMut.mutate(a.id)}
                disabled={approveMut.isPending}
                className="bg-status-approved-accent text-white rounded px-3 py-1.5 text-xs hover:opacity-90 disabled:opacity-60"
              >
                {approveMut.isPending && approveMut.variables === a.id ? 'Approving…' : 'Approve'}
              </button>
              <button
                onClick={() => toast.info('Revision flow — TODO')}
                className="bg-transparent border border-border rounded px-3 py-1.5 text-xs text-ink-secondary hover:bg-surface-muted"
              >
                Request revision
              </button>
            </div>
          </article>
        ))}
        {!isLoading && pending.length === 0 && (
          <div className="text-xs text-ink-tertiary py-4 text-center">Queue clear.</div>
        )}
      </div>
    </section>
  )
}
